import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Menu, X } from 'lucide-react';
import './PhotoGallery.css';

const mediaOrigin = (process.env.REACT_APP_GALLERY_MEDIA_ORIGIN || 'https://media.have-fun.engineering').replace(/\/$/, '');
const proxyVersion = '3';

const photoIdentity = (photo) => photo.key;
const photoUrl = (photo, variant = 'preview') => {
  const encodedKey = photo.key.split('/').map(encodeURIComponent).join('/');
  const suffix = variant === 'preview' ? `?v=${proxyVersion}` : '';
  return `${mediaOrigin}/${variant}/${encodedKey}${suffix}`;
};
const galleryCoverUrl = (gallery) => photoUrl({ key: gallery.coverKey });
const galleryFromHash = (galleries) => {
  const match = window.location.hash.match(/^#\/galleries\/([^/]+)/);
  return galleries.find((gallery) => gallery.slug === match?.[1]) || null;
};

export default function PhotoGallery() {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('photo-site');
    document.title = 'have fun photography';
    return () => document.documentElement.classList.remove('photo-site');
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${mediaOrigin}/galleries`, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Could not load galleries'))))
      .then((data) => {
        if (!Array.isArray(data.galleries)) throw new Error('Invalid gallery response');
        setGalleries(data.galleries);
        setSelectedGallery(galleryFromHash(data.galleries) || (data.galleries.length === 1 ? data.galleries[0] : null));
      })
      .catch((error) => {
        if (error.name !== 'AbortError') setLoadError(true);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      setSelectedGallery(galleryFromHash(galleries));
      setSelectedPhoto(null);
      setMenuOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [galleries]);

  const activeIndex = useMemo(
    () => selectedGallery?.photos.findIndex((photo) => photoIdentity(photo) === photoIdentity(selectedPhoto || {})) ?? -1,
    [selectedGallery, selectedPhoto],
  );

  const navigatePhoto = (direction) => {
    if (!selectedGallery) return;
    const next = (activeIndex + direction + selectedGallery.photos.length) % selectedGallery.photos.length;
    setSelectedPhoto(selectedGallery.photos[next]);
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!selectedPhoto) return;
      if (event.key === 'Escape') setSelectedPhoto(null);
      if (event.key === 'ArrowLeft') navigatePhoto(-1);
      if (event.key === 'ArrowRight') navigatePhoto(1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  return (
    <main className="gallery-app">
      <header className="site-header">
        <a className="wordmark" href="#/" aria-label="All galleries">have fun <span>photography</span></a>
        <button className="menu-toggle" type="button" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen}>
          {menuOpen ? <X size={21} /> : <Menu size={21} />} <span>Galleries</span>
        </button>
        {menuOpen && <nav className="gallery-menu" aria-label="Galleries">
          {galleries.map((gallery) => <a key={gallery.slug} href={`#/galleries/${gallery.slug}`}><span>{gallery.title}</span><small>{gallery.date}</small></a>)}
        </nav>}
      </header>

      {loadError ? <div className="gallery-loading" role="alert">Gallery unavailable. Please try again.</div> : selectedGallery
        ? <GalleryView key={selectedGallery.slug} gallery={selectedGallery} onBack={() => { window.location.hash = '/'; }} onSelectPhoto={setSelectedPhoto} />
        : <GalleryIndex galleries={galleries} onOpenGallery={(gallery) => { window.location.hash = `/galleries/${gallery.slug}`; }} />}

      {selectedPhoto && selectedGallery && <PhotoViewer
        photo={selectedPhoto}
        gallery={selectedGallery}
        position={activeIndex + 1}
        onClose={() => setSelectedPhoto(null)}
        onPrevious={() => navigatePhoto(-1)}
        onNext={() => navigatePhoto(1)}
      />}
    </main>
  );
}

function GalleryIndex({ galleries, onOpenGallery }) {
  if (galleries.length === 0) return <div className="gallery-loading" aria-live="polite">Loading gallery</div>;
  return <section className="gallery-list" aria-label="Photo galleries">
    {galleries.map((gallery, index) => <button className="gallery-card" type="button" key={gallery.slug} onClick={() => onOpenGallery(gallery)}>
      <img src={galleryCoverUrl(gallery)} alt="" />
      <span className="gallery-card-overlay" />
      <span className="gallery-number">{String(index + 1).padStart(2, '0')}</span>
      <span className="gallery-card-copy"><small>{gallery.date || `${gallery.photos.length} photographs`}</small><strong>{gallery.title}</strong><span>Open gallery <ArrowLeft size={17} /></span></span>
    </button>)}
  </section>;
}

function GalleryView({ gallery, onBack, onSelectPhoto }) {
  const allProxiesReady = gallery.photos.every((photo) => photo.proxyReady);
  const maxConcurrentLoads = allProxiesReady ? Math.min(8, gallery.photos.length) : 1;
  const queueDelay = allProxiesReady ? 40 : 1000;
  const [requestedPhotos, setRequestedPhotos] = useState(() => Math.min(maxConcurrentLoads, gallery.photos.length));
  const requestNextPhoto = () => window.setTimeout(() => setRequestedPhotos((count) => Math.min(count + 1, gallery.photos.length)), queueDelay);

  return <section className="gallery-view">
    <div className="gallery-heading">
      <button className="back-button" type="button" onClick={onBack}><ArrowLeft size={18} /> All galleries</button>
      <div><p className="eyebrow">{gallery.date ? `${gallery.date} · ` : ''}{gallery.photos.length} photographs</p><h1>{gallery.title}</h1></div>
    </div>
    <div className="photo-grid">{gallery.photos.map((photo, index) => <PhotoTile
      key={photoIdentity(photo)}
      photo={photo}
      index={index}
      shouldLoad={index < requestedPhotos}
      onSettled={requestNextPhoto}
      onSelectPhoto={onSelectPhoto}
    />)}</div>
  </section>;
}

function PhotoTile({ photo, index, shouldLoad, onSettled, onSelectPhoto }) {
  const maxRetries = 8;
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [settled, setSettled] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [aspectRatio, setAspectRatio] = useState(null);
  const settle = () => {
    if (settled) return;
    setSettled(true);
    onSettled();
  };
  const retry = () => {
    if (attempt >= maxRetries) {
      setFailed(true);
      settle();
      return;
    }
    window.setTimeout(() => setAttempt((value) => value + 1), Math.min(15000, 1000 * 2 ** attempt));
  };
  const source = `${photoUrl(photo)}${attempt ? `&retry=${attempt}` : ''}`;
  const shape = !aspectRatio ? '' : aspectRatio < 0.82 ? 'is-portrait' : aspectRatio < 1.15 ? 'is-square' : aspectRatio > 1.65 ? 'is-wide' : 'is-landscape';

  return <article className={`photo-tile tile-${(index % 5) + 1} ${loaded ? `is-loaded ${shape}` : ''}`} style={aspectRatio ? { '--photo-ratio': aspectRatio } : undefined}>
    <button type="button" onClick={() => onSelectPhoto(photo)} aria-label={`View ${photo.alt}`}>
      {!loaded && !failed && <span className="image-loader" aria-label="Loading photo" />}
      {failed && <span className="image-error">Could not load photo</span>}
      {shouldLoad && <img src={source} alt={photo.alt} decoding="async" onLoad={(event) => {
        const { naturalHeight, naturalWidth } = event.currentTarget;
        if (naturalWidth && naturalHeight) setAspectRatio(naturalWidth / naturalHeight);
        setLoaded(true);
        settle();
      }} onError={retry} />}
      <span className="view-photo">View <span>↗</span></span>
    </button>
    <a href={photoUrl(photo, 'download')} download aria-label={`Download ${photo.alt}`} className="download-button"><Download size={16} /></a>
  </article>;
}

function PhotoViewer({ photo, gallery, position, onClose, onPrevious, onNext }) {
  const [loaded, setLoaded] = useState(false);
  const [showGestureHint, setShowGestureHint] = useState(true);
  const touchStart = useRef(null);
  useEffect(() => setLoaded(false), [photo]);
  useEffect(() => {
    const timeout = window.setTimeout(() => setShowGestureHint(false), 3600);
    return () => window.clearTimeout(timeout);
  }, []);
  useEffect(() => {
    const { overflow, paddingRight } = document.body.style;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth) document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
    };
  }, []);
  const handleTouchStart = (event) => {
    setShowGestureHint(false);
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };
  const handleTouchEnd = (event) => {
    if (!touchStart.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStart.current.x;
    const deltaY = touch.clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY)) deltaX < 0 ? onNext() : onPrevious();
    else if (deltaY > 72 && Math.abs(deltaY) > Math.abs(deltaX)) onClose();
  };

  return <div className="viewer" role="dialog" aria-modal="true" aria-label={photo.alt} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
    <button className="viewer-close" type="button" onClick={onClose} aria-label="Close viewer"><X size={24} /></button>
    <span className="viewer-count">{position} / {gallery.photos.length}</span>
    <a className="viewer-download" href={photoUrl(photo, 'download')} download><Download size={17} /> Download full resolution</a>
    <span className={`viewer-gesture-hint ${showGestureHint ? 'is-visible' : ''}`} aria-hidden="true"><span>←&nbsp;&nbsp; Swipe to browse &nbsp;&nbsp;→</span><span>↓ Swipe down to close</span></span>
    <button className="viewer-nav viewer-prev" type="button" onClick={onPrevious} aria-label="Previous photo"><ChevronLeft size={32} /></button>
    {!loaded && <span className="viewer-loader" aria-label="Loading full-screen preview" />}
    <img src={photoUrl(photo)} alt={photo.alt} onLoad={() => setLoaded(true)} />
    <button className="viewer-nav viewer-next" type="button" onClick={onNext} aria-label="Next photo"><ChevronRight size={32} /></button>
  </div>;
}
